import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiEdit2,
  FiMapPin,
  FiPlus,
  FiSearch,
  FiTrash2,
} from "react-icons/fi";
import StoreModal from "../../components/masters/StoreModal";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import ClientPagination, { useClientPagination } from "../../components/common/ClientPagination";

export default function StoresPage() {
  const { accessToken } = useAuth();
  const [stores, setStores] = useState([]);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStore, setEditingStore] = useState(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const [storesResponse, branchesResponse] = await Promise.all([
        apiRequest("/stores", { accessToken }),
        apiRequest("/branches", { accessToken }),
      ]);

      setStores(Array.isArray(storesResponse) ? storesResponse : storesResponse?.data || []);
      setBranches(Array.isArray(branchesResponse) ? branchesResponse : branchesResponse?.data || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load stores.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredStores = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return stores;

    return stores.filter((store) =>
      [
        store.name,
        store.code,
        store.type,
        store.city,
        store.branch?.name,
        store.branch?.code,
      ].some((value) => value?.toLowerCase().includes(query)),
    );
  }, [stores, search]);

  const stats = useMemo(() => {
    const retailStores = stores.filter((store) => store.type === "STORE").length;
    const warehouses = stores.filter((store) => store.type === "WAREHOUSE").length;
    const active = stores.filter((store) => store.isActive).length;

    return { total: stores.length, retailStores, warehouses, active };
  }, [stores]);
  const storesPagination = useClientPagination(filteredStores);

  const handleSave = async (form) => {
    try {
      setError("");

      if (editingStore) {
        await apiRequest(`/stores/${editingStore.id}`, {
          method: "PATCH",
          accessToken,
          body: form,
        });
      } else {
        const { isActive, ...createPayload } = form;

        await apiRequest("/stores", {
          method: "POST",
          accessToken,
          body: createPayload,
        });
      }

      setShowModal(false);
      setEditingStore(null);
      await loadData();
    } catch (requestError) {
      setError(requestError.message || "Unable to save store.");
    }
  };

  const handleDelete = async (store) => {
    if (!window.confirm(`Delete "${store.name}"?`)) return;

    try {
      setError("");
      await apiRequest(`/stores/${store.id}`, {
        method: "DELETE",
        accessToken,
      });
      await loadData();
    } catch (requestError) {
      setError(requestError.message || "Unable to delete store.");
    }
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">Masters / Stores</p>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Stores & Warehouses
          </h1>
          <p className="mt-2 text-gray-500">
            Manage branch locations, retail stores, and warehouses.
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingStore(null);
            setShowModal(true);
          }}
          className="flex h-11 items-center justify-center gap-2 rounded-xl bg-primary-800 px-5 text-white hover:bg-primary-900"
        >
          <FiPlus />
          Add Store
        </button>
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-8 grid gap-5 md:grid-cols-4">
        {[
          ["Total Locations", stats.total, "text-gray-900"],
          ["Retail Stores", stats.retailStores, "text-blue-600"],
          ["Warehouses", stats.warehouses, "text-orange-600"],
          ["Active Locations", stats.active, "text-green-600"],
        ].map(([label, value, color]) => (
          <div key={label} className="rounded-3xl border bg-white p-6">
            <p className="text-sm text-gray-500">{label}</p>
            <h2 className={`mt-2 text-3xl font-bold ${color}`}>{value}</h2>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="border-b border-gray-100 p-5">
          <div className="relative max-w-md">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search stores, branches, or warehouses..."
              className="h-12 w-full rounded-xl border border-gray-200 pl-12 pr-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[950px]">
            <thead>
              <tr className="border-b bg-gray-50 text-left text-xs uppercase text-gray-500">
                <th className="px-6 py-4">Location</th>
                <th className="px-6 py-4">Code</th>
                <th className="px-6 py-4">Branch</th>
                <th className="px-6 py-4">Phone</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-sm text-gray-500">
                    Loading stores...
                  </td>
                </tr>
              ) : filteredStores.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-sm text-gray-500">
                    No stores found.
                  </td>
                </tr>
              ) : (
                storesPagination.pageItems.map((store) => (
                  <tr key={store.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
                          <FiMapPin size={20} />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{store.name}</p>
                          <p className="text-xs text-gray-500">{store.city || "Location not set"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-mono text-sm">{store.code}</td>
                    <td className="px-6 py-4 text-sm">
                      {store.branch ? (
                        <>
                          <p className="font-medium">{store.branch.name}</p>
                          <p className="text-xs text-gray-500">{store.branch.code}</p>
                        </>
                      ) : (
                        <span className="text-gray-400">Central / Unassigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">{store.phone || "—"}</td>
                    <td className="px-6 py-4">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        store.type === "WAREHOUSE"
                          ? "bg-orange-100 text-orange-700"
                          : "bg-blue-100 text-blue-700"
                      }`}>
                        {store.type === "WAREHOUSE" ? "Warehouse" : "Retail Store"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        store.isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-600"
                      }`}>
                        {store.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingStore(store);
                            setShowModal(true);
                          }}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border text-gray-600 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(store)}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border text-red-500 hover:border-red-200 hover:bg-red-50"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <ClientPagination pagination={storesPagination} label="stores" />
      </div>

      <StoreModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingStore(null);
        }}
        onSave={handleSave}
        initialData={editingStore}
        branches={branches}
      />
    </div>
  );
}
